// TAXI Calculator — internal, isolated dispatch pricing tool (Updates 3 + 4).
//
// A SEPARATE sibling component rendered on the admin Book Ride page, OUTSIDE the
// booking form. It has its own state, validation, API requests and error
// handling, and NEVER reads from or writes to the booking form. There is no
// "Apply Total to Booking" action. Styling is Tailwind-utility-scoped only.
//
// Update 4 · The same component powers a DETACHED calculator-only window
// (route /admin/taxi-calculator, rendered with `detached`). Embedded and
// detached instances stay in sync over a same-origin BroadcastChannel, seeded
// by a session-scoped snapshot. Customer info is only ever held in
// sessionStorage (session-scoped, never localStorage / never permanent).
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Calculator, ChevronDown, ChevronUp, Copy, ClipboardList,
  RotateCcw, ExternalLink, Loader2, AlertTriangle, ArrowLeft, MonitorUp,
  Printer, History, SlidersHorizontal, Trash2, Mail, Send,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useAdmin } from "@/contexts/AdminContext";
import RateEditorDialog from "@/components/admin/RateEditorDialog";
import SpecialRateCalculator from "@/components/admin/SpecialRateCalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALLOWED_ROLES = new Set([
  "admin", "supervisor", "dispatcher", "operating_partner",
  "developer", "general_partner", "team_leader", "field_supervisor",
]);

const CATEGORY_LABEL = {
  weatherford_in_city: "Weatherford (in-city)",
  regional: "Regional destination",
  long_distance: "Long-distance destination",
  unlisted_oklahoma: "Unlisted Oklahoma (formula)",
  manual_quote_required: "Manual Quote Required",
};

const OPT_WEATHERFORD = "__weatherford__";
const OPT_UNLISTED_OK = "__unlisted_ok__";
const OPT_MANUAL = "__manual__";

// Update 4 · same-origin sync primitives
const CHANNEL_NAME = "rtg-taxi-calc";
const SESSION_KEY = "rtg_taxi_calc_state";     // session-scoped snapshot (not permanent)
const POPUP_NAME = "rtgTaxiCalculatorWindow";  // fixed name → one window, reused/focused
const POPUP_PATH = "/admin/taxi-calculator";

const usd = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n));

const DEFAULT_FORM = {
  pickup: "",
  destination: "",
  destinationKey: OPT_WEATHERFORD,
  tripType: "one_way", // "one_way" | "round_trip"
  passengers: "1",
  oneWayMiles: "",
  estMinutes: "3",
  waitingMinutes: "0",
  extraStops: "0",
  extraStopLocations: "",
  fuelPrice: "",
  fuelProfile: "combined",
  notes: "",
};

function readSnapshot() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

export default function TaxiCalculator({ detached = false }) {
  const { admin } = useAdmin();
  const snapshot = useRef(readSnapshot());

  const [expanded, setExpanded] = useState(detached); // detached is always open
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(() => snapshot.current?.form || DEFAULT_FORM);
  const [result, setResult] = useState(() => snapshot.current?.result || null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Enhancements · session quote history + supervisor rate editor
  const [history, setHistory] = useState(() => snapshot.current?.history || []);
  const [rateEditorOpen, setRateEditorOpen] = useState(false);
  // Email-quote UI (recompute + send happen server-side)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  // Update 4 · window/sync bookkeeping
  const [detachedOpen, setDetachedOpen] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const chanRef = useRef(null);
  const winIdRef = useRef(Math.random().toString(36).slice(2));
  const applyingRemoteRef = useRef(false);
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const stateRef = useRef({ form, result });

  // --- Authorization: dispatchers / supervisors / administrators only -------
  const authorized = useMemo(() => {
    if (!admin) return false;
    const roles = new Set(
      [admin.role, admin.canonical_role, ...(Array.isArray(admin.roles) ? admin.roles : [])].filter(Boolean)
    );
    if (roles.size === 0) return true; // staff already inside the admin console
    return [...roles].some((r) => ALLOWED_ROLES.has(r));
  }, [admin]);

  // Keep a live ref of state so the channel handler always answers with fresh data.
  useEffect(() => { stateRef.current = { form, result, history }; }, [form, result, history]);

  // --- Load pricing config once (selector options, fuel profiles, version) --
  useEffect(() => {
    if (!authorized || config) return;
    let cancelled = false;
    adminApi
      .get("/taxi-calculator/config")
      .then(({ data }) => {
        if (cancelled) return;
        setConfig(data);
        // Only seed fuel defaults when the field is still blank so a synced /
        // restored fuel price is never clobbered.
        setForm((f) =>
          f.fuelPrice === ""
            ? { ...f, fuelPrice: String(data.fuel_baseline_price ?? ""), fuelProfile: f.fuelProfile || data.default_fuel_profile || "combined" }
            : f
        );
      })
      .catch(() => { if (!cancelled) setError("Could not load pricing configuration."); });
    return () => { cancelled = true; };
  }, [authorized, config]);

  // --- Persist + broadcast state (Update 4) ---------------------------------
  // Always write the session snapshot (so a reload or a freshly-opened popup
  // inherits the latest values); only *broadcast* local edits to avoid loops.
  useEffect(() => {
    if (!authorized) return;
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ form, result, history })); } catch { /* ignore */ }
    if (applyingRemoteRef.current) { applyingRemoteRef.current = false; return; }
    try { chanRef.current?.postMessage({ type: "state", from: winIdRef.current, form, result, history }); } catch { /* ignore */ }
  }, [form, result, history, authorized]);

  // --- BroadcastChannel wiring (Update 4) -----------------------------------
  useEffect(() => {
    if (!authorized) return undefined;
    let chan = null;
    try { chan = new BroadcastChannel(CHANNEL_NAME); } catch { chan = null; }
    chanRef.current = chan;
    if (!chan) return undefined;

    chan.onmessage = (ev) => {
      const msg = ev?.data || {};
      if (!msg || msg.from === winIdRef.current) return;
      if (msg.type === "state") {
        applyingRemoteRef.current = true;
        setForm(msg.form || DEFAULT_FORM);
        setResult(msg.result ?? null);
        if (Array.isArray(msg.history)) setHistory(msg.history);
      } else if (msg.type === "config-updated") {
        // A supervisor edited the rate table in another window — refresh config.
        adminApi.get("/taxi-calculator/config").then(({ data }) => setConfig(data)).catch(() => {});
      } else if (msg.type === "request-state") {
        try { chan.postMessage({ type: "state", from: winIdRef.current, ...stateRef.current }); } catch { /* ignore */ }
      } else if (msg.type === "focus-embedded" && !detached) {
        setExpanded(true);
        try { window.focus(); } catch { /* ignore */ }
      } else if (msg.type === "popup-open" && !detached) {
        setDetachedOpen(true);
      } else if (msg.type === "popup-closing" && !detached) {
        setDetachedOpen(false);
        popupRef.current = null;
      }
    };

    if (detached) {
      // Ask the embedded instance for the freshest state, and announce presence.
      try { chan.postMessage({ type: "request-state", from: winIdRef.current }); } catch { /* ignore */ }
      try { chan.postMessage({ type: "popup-open", from: winIdRef.current }); } catch { /* ignore */ }
    }
    return () => { try { chan.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, detached]);

  // Detached window: tell the console when we're closing (belt-and-suspenders
  // alongside the opener's close-poll) so the embedded indicator clears fast.
  useEffect(() => {
    if (!detached) return undefined;
    const onUnload = () => {
      try { chanRef.current?.postMessage({ type: "popup-closing", from: winIdRef.current }); } catch { /* ignore */ }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [detached]);

  // Cleanup the close-poll on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (!authorized) return null;

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const selectedFixed = config?.destinations?.find((d) => d.key === form.destinationKey) || null;
  const isFixed = !!selectedFixed;
  const isUnlistedOk = form.destinationKey === OPT_UNLISTED_OK;
  const isWeatherford = form.destinationKey === OPT_WEATHERFORD;
  const milesDisabled = isFixed; // fixed destinations use their table mileage

  const buildPayload = () => {
    const passengers = Math.max(1, parseInt(form.passengers || "1", 10) || 1);
    const roundTrip = form.tripType === "round_trip";
    const payload = {
      passengers,
      round_trip: roundTrip,
      extra_stops: Math.max(0, parseInt(form.extraStops || "0", 10) || 0),
      waiting_minutes: Math.max(0, parseInt(form.waitingMinutes || "0", 10) || 0),
      estimated_duration_minutes: Math.max(3, parseInt(form.estMinutes || "3", 10) || 3),
      fuel_profile: form.fuelProfile,
      current_fuel_price: form.fuelPrice === "" ? null : Number(form.fuelPrice),
      one_way_miles: form.oneWayMiles === "" ? 0 : Number(form.oneWayMiles),
      origin_in_weatherford: false,
      destination_in_weatherford: false,
      destination_name: null,
      destination_state: "OK",
    };
    if (isWeatherford) {
      payload.origin_in_weatherford = true;
      payload.destination_in_weatherford = true;
    } else if (isFixed) {
      payload.destination_name = selectedFixed.match_value;
      payload.destination_state = selectedFixed.state || "OK";
      payload.one_way_miles = selectedFixed.miles; // table mileage drives fuel
    } else if (isUnlistedOk) {
      payload.destination_state = "OK";
    } else {
      payload.destination_state = "OUT_OF_STATE"; // manual quote (never OK formula)
    }
    return payload;
  };

  const validate = () => {
    if (isUnlistedOk) {
      const m = Number(form.oneWayMiles);
      if (!form.oneWayMiles || Number.isNaN(m) || m <= 0) {
        return "Enter a one-way distance (miles) for an unlisted Oklahoma destination.";
      }
    }
    if (form.fuelPrice !== "" && Number(form.fuelPrice) < 0) {
      return "Current fuel price cannot be negative.";
    }
    return "";
  };

  const calculate = async () => {
    setError("");
    const v = validate();
    if (v) { setError(v); setResult(null); return; }
    setLoading(true);
    try {
      const { data } = await adminApi.post("/taxi-calculator/quote", buildPayload());
      setResult(data);
      // Session quote history (newest first, capped at 8).
      const label = data.manual_quote_required
        ? "Manual Quote Required"
        : `${CATEGORY_LABEL[data.category] || data.category}${data.matched_destination ? ` · ${data.matched_destination}` : ""}`;
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        at: new Date().toISOString(),
        label,
        total: data.manual_quote_required ? null : data.total,
        breakdown: buildBreakdownText(data),
      };
      setHistory((h) => [entry, ...h].slice(0, 8));
    } catch (e) {
      setError(e?.response?.data?.detail || "Calculation failed. Please try again.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — clipboard unavailable");
    }
  };

  const copyTotal = () => {
    if (!result) return;
    const t = result.manual_quote_required ? "Manual Quote Required" : usd(result.total);
    copyText(t, "Total");
  };

  const buildBreakdownText = (res = result) => {
    if (!res) return "";
    const lit = res.line_items || {};
    const lines = [
      "TAXI Calculator — Fare Breakdown",
      `Pricing version: ${res.pricing_version} (effective ${res.effective_date})`,
      form.pickup ? `Pickup: ${form.pickup}` : null,
      form.destination ? `Destination: ${form.destination}` : null,
      `Pricing category: ${CATEGORY_LABEL[res.category] || res.category}`,
      res.matched_destination ? `Destination rate: ${res.matched_destination}` : null,
    ];
    if (res.manual_quote_required) {
      lines.push("Result: MANUAL QUOTE REQUIRED");
    } else {
      lines.push(
        `One-way distance: ${res.one_way_miles} mi`,
        `Total billable distance: ${res.total_trip_miles} mi`,
        `Estimated trip time: ${res.estimated_duration_minutes} min (informational)`,
        `Base fare: ${usd(lit.base_fare)}`,
        `Additional-passenger charge: ${usd(lit.additional_passenger_charge)} (${res.additional_passengers} extra)`,
        `Round-trip: ${res.round_trip ? `yes ×${lit.round_trip_multiplier}` : "no"} → ${usd(lit.base_and_passengers_billed)}`,
        `Fuel surcharge: ${usd(lit.fuel_surcharge?.amount)}`,
        `Waiting-time charge: ${usd(lit.waiting_time?.amount)}`,
        `Extra-stop charge: ${usd(lit.extra_stops?.amount)}`,
        `FINAL TOTAL: ${usd(res.total)}`,
      );
    }
    if (form.extraStopLocations.trim()) lines.push(`Extra stops: ${form.extraStopLocations.trim()}`);
    if (form.notes.trim()) lines.push(`Notes: ${form.notes.trim()}`);
    return lines.filter(Boolean).join("\n");
  };

  const copyBreakdown = () => {
    if (!result) return;
    copyText(buildBreakdownText(), "Fare breakdown");
  };

  const resetCalculator = () => {
    setForm({
      ...DEFAULT_FORM,
      fuelPrice: String(config?.fuel_baseline_price ?? ""),
      fuelProfile: config?.default_fuel_profile || "combined",
    });
    setResult(null);
    setError("");
  };

  // --- Rate editor save callback (supervisor) -------------------------------
  const onRatesSaved = (newConfig) => {
    setConfig(newConfig);
    // If the current fuel price still equals the OLD baseline default, keep it
    // aligned to the new baseline (only when untouched by the operator).
    try { chanRef.current?.postMessage({ type: "config-updated", from: winIdRef.current }); } catch { /* ignore */ }
    toast.success(`Rates saved · pricing v${newConfig.pricing_version}`);
  };

  // --- Quote history helpers ------------------------------------------------
  const copyHistoryTotal = (e) => copyText(e.total === null ? "Manual Quote Required" : usd(e.total), "Total");
  const copyHistoryDetails = (e) => copyText(e.breakdown || "", "Fare breakdown");
  const clearHistory = () => setHistory([]);

  // --- Quick print (printable fare-quote slip) ------------------------------
  const escapeHtml = (s) =>
    String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  const buildSlipHtml = () => {
    const r = result;
    const L = r.line_items || {};
    const rows = r.manual_quote_required
      ? `<tr><td colspan="2" class="mq">Manual Quote Required</td></tr>`
      : [
          ["Pricing category", (CATEGORY_LABEL[r.category] || r.category) + (r.matched_destination ? ` · ${r.matched_destination}` : "")],
          ["One-way distance", `${r.one_way_miles} mi`],
          ["Total billable distance", `${r.total_trip_miles} mi`],
          ["Estimated trip time", `${r.estimated_duration_minutes} min`],
          ["Base fare", usd(L.base_fare)],
          [`Additional passengers (${r.additional_passengers})`, usd(L.additional_passenger_charge)],
          r.round_trip ? [`Round-trip (×${L.round_trip_multiplier})`, usd(L.base_and_passengers_billed)] : null,
          ["Fuel surcharge", usd(L.fuel_surcharge?.amount)],
          ["Waiting-time charge", usd(L.waiting_time?.amount)],
          ["Extra-stop charge", usd(L.extra_stops?.amount)],
        ].filter(Boolean).map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td class="r">${escapeHtml(v)}</td></tr>`).join("");
    return `<!doctype html><html><head><meta charset="utf-8"><title>Buller Taxi — Fare Quote</title>
<style>body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}h1{font-size:18px;margin:0}
.sub{color:#666;font-size:12px;margin:2px 0 14px}table{width:100%;border-collapse:collapse;font-size:13px}
td{padding:6px 4px;border-bottom:1px solid #eee}td.r{text-align:right;font-weight:600}
.total{margin-top:14px;padding-top:10px;border-top:2px solid #111;display:flex;justify-content:space-between;font-size:18px;font-weight:800}
.mq{color:#b45309;font-weight:800;text-align:center;font-size:16px;padding:16px 0}
.meta{font-size:11px;color:#888;margin-top:16px}.trip{font-size:13px;margin:8px 0}@media print{button{display:none}}</style>
</head><body>
<h1>Buller Taxi Services</h1>
<div class="sub">Fare Quote · ${escapeHtml(new Date().toLocaleString())}</div>
${(form.pickup || form.destination) ? `<div class="trip"><b>From:</b> ${escapeHtml(form.pickup || "—")}<br><b>To:</b> ${escapeHtml(form.destination || "—")}</div>` : ""}
<table>${rows}</table>
${r.manual_quote_required ? "" : `<div class="total"><span>Total</span><span>${escapeHtml(usd(r.total))}</span></div>`}
${form.notes.trim() ? `<div class="trip"><b>Notes:</b> ${escapeHtml(form.notes.trim())}</div>` : ""}
<div class="meta">Pricing version ${escapeHtml(r.pricing_version)} · effective ${escapeHtml(r.effective_date)}. Estimate only; final fare may vary with actual distance, stops, and wait time.</div>
</body></html>`;
  };

  const printQuote = () => {
    if (!result) return;
    const w = window.open("", "taxiQuoteSlip", "width=460,height=680");
    if (!w) { toast.error("Popup blocked — allow pop-ups to print the quote"); return; }
    w.document.write(buildSlipHtml());
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 250);
  };

  // --- Email quote (server recomputes + sends via Resend) -------------------
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const sendEmailQuote = async () => {
    if (!result) return;
    const to = emailTo.trim();
    if (!EMAIL_RE.test(to)) { toast.error("Enter a valid email address"); return; }
    setEmailSending(true);
    try {
      const { data } = await adminApi.post("/taxi-calculator/email-quote", {
        ...buildPayload(),
        recipient_email: to,
        pickup_label: form.pickup || null,
        destination_label: form.destination || null,
        notes: form.notes || null,
      });
      toast.success(`Quote emailed to ${data.sent_to}`);
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the quote email.");
    } finally {
      setEmailSending(false);
    }
  };

  // --- Update 4 · open / focus the detached window (embedded only) ----------
  const openDetached = () => {
    setPopupBlocked(false);
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.focus(); } catch { /* ignore */ }
      return;
    }
    const url = `${window.location.origin}${POPUP_PATH}`;
    const features = "popup=yes,width=540,height=780,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes";
    let win = null;
    try { win = window.open(url, POPUP_NAME, features); } catch { win = null; }
    if (!win) { setPopupBlocked(true); return; } // popup blocked → keep embedded, inline notice
    popupRef.current = win;
    setDetachedOpen(true);
    try { win.focus(); } catch { /* ignore */ }
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        clearInterval(pollRef.current); pollRef.current = null;
        popupRef.current = null;
        setDetachedOpen(false);
        // Embedded is already synced via the channel; also re-hydrate from the
        // session snapshot as a safety net, then surface the calculator.
        const snap = readSnapshot();
        if (snap) {
          applyingRemoteRef.current = true;
          setForm(snap.form || DEFAULT_FORM);
          setResult(snap.result ?? null);
        }
        setExpanded(true);
      }
    }, 700);
  };

  // --- Update 4 · return to the admin console (detached only) ---------------
  const returnToConsole = () => {
    try { chanRef.current?.postMessage({ type: "state", from: winIdRef.current, form, result }); } catch { /* ignore */ }
    try { chanRef.current?.postMessage({ type: "focus-embedded", from: winIdRef.current }); } catch { /* ignore */ }
    try { chanRef.current?.postMessage({ type: "popup-closing", from: winIdRef.current }); } catch { /* ignore */ }
    const opener = window.opener;
    if (opener && !opener.closed) { try { opener.focus(); } catch { /* ignore */ } }
    // Defer the close one tick so the BroadcastChannel messages flush first
    // (some browsers, e.g. Safari, drop them if window.close() fires
    // synchronously). If the window can't self-close it simply stays put —
    // the console is already focused and state is synced.
    setTimeout(() => { try { window.close(); } catch { /* ignore */ } }, 60);
  };

  const li = result?.line_items || {};
  const bodyOpen = detached || expanded;

  return (
    <section
      data-testid="taxi-calculator"
      className={`${detached ? "" : "mt-6"} rounded-xl border-2 border-indigo-400/40 bg-gradient-to-br from-indigo-950/30 via-zinc-950 to-zinc-950 shadow-lg shadow-indigo-950/20`}
    >
      {/* Header / collapse toggle */}
      <button
        type="button"
        data-testid="taxi-calc-toggle"
        onClick={() => { if (!detached) setExpanded((v) => !v); }}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2">
          <span className="grid place-items-center h-8 w-8 rounded-lg bg-indigo-400/20 border border-indigo-400/50">
            <Calculator className="h-4 w-4 text-indigo-200" />
          </span>
          <span>
            <span className="block text-sm font-black uppercase tracking-widest text-indigo-100">TAXI Calculator</span>
            <span className="block text-[10px] text-zinc-400">
              {detached
                ? "Detached window · synced with the admin console"
                : "Independent pricing tool · does not read or change the booking form"}
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-indigo-300">
          {config?.pricing_version ? `v${config.pricing_version}` : ""}
          {!detached && (expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
        </span>
      </button>

      {bodyOpen && (
        <div className="px-4 pb-4 pt-1 border-t border-indigo-400/20" data-testid="taxi-calc-body">
          {/* Detached indicator (embedded only) */}
          {!detached && detachedOpen && (
            <div data-testid="taxi-calc-detached-indicator" className="mt-2 flex items-center gap-2 rounded-md border border-indigo-400/40 bg-indigo-400/10 px-3 py-1.5 text-[11px] text-indigo-200">
              <MonitorUp className="h-3.5 w-3.5" /> Calculator is open in a separate window — values stay in sync.
            </div>
          )}

          {/* ---- Inputs ---- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Pickup city / location</Label>
              <Input
                data-testid="taxi-calc-pickup"
                value={form.pickup}
                onChange={(e) => setField("pickup", e.target.value)}
                placeholder="e.g. Weatherford, OK"
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Destination city / location</Label>
              <Input
                data-testid="taxi-calc-destination"
                value={form.destination}
                onChange={(e) => setField("destination", e.target.value)}
                placeholder="e.g. Clinton, OK"
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Pricing destination</Label>
              <Select value={form.destinationKey} onValueChange={(v) => setField("destinationKey", v)}>
                <SelectTrigger data-testid="taxi-calc-destination-select" className="bg-zinc-950 border-zinc-700 h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-h-80">
                  <SelectItem value={OPT_WEATHERFORD}>Weatherford (in-city) · $10 flat</SelectItem>
                  {(config?.destinations || []).map((d) => (
                    <SelectItem key={d.key} value={d.key}>
                      {d.display} · {usd(d.base_fare)} ({d.miles} mi)
                    </SelectItem>
                  ))}
                  <SelectItem value={OPT_UNLISTED_OK}>Unlisted Oklahoma (formula)</SelectItem>
                  <SelectItem value={OPT_MANUAL}>Unlisted out-of-state (Manual Quote)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Trip type</Label>
              <Select value={form.tripType} onValueChange={(v) => setField("tripType", v)}>
                <SelectTrigger data-testid="taxi-calc-trip-type" className="bg-zinc-950 border-zinc-700 h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                  <SelectItem value="one_way">One-way</SelectItem>
                  <SelectItem value="round_trip">Round-trip (25% return discount)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Passengers (min 1)</Label>
              <Input
                data-testid="taxi-calc-passengers"
                type="number" min="1" step="1"
                value={form.passengers}
                onChange={(e) => setField("passengers", e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">
                One-way distance (mi){milesDisabled ? " · fixed by destination" : ""}
              </Label>
              <Input
                data-testid="taxi-calc-miles"
                type="number" min="0" step="0.1"
                value={milesDisabled ? String(selectedFixed.miles) : form.oneWayMiles}
                onChange={(e) => setField("oneWayMiles", e.target.value)}
                disabled={milesDisabled}
                placeholder={isUnlistedOk ? "required" : "optional"}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1 disabled:opacity-60"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Estimated trip time (min ≥ 3)</Label>
              <Input
                data-testid="taxi-calc-duration"
                type="number" min="3" step="1"
                value={form.estMinutes}
                onChange={(e) => setField("estMinutes", e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Estimated waiting time (min)</Label>
              <Input
                data-testid="taxi-calc-waiting"
                type="number" min="0" step="1"
                value={form.waitingMinutes}
                onChange={(e) => setField("waitingMinutes", e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Extra-stop count</Label>
              <Input
                data-testid="taxi-calc-stops"
                type="number" min="0" step="1"
                value={form.extraStops}
                onChange={(e) => setField("extraStops", e.target.value)}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Current fuel price ($/gal)</Label>
              <Input
                data-testid="taxi-calc-fuel-price"
                type="number" min="0" step="0.01"
                value={form.fuelPrice}
                onChange={(e) => setField("fuelPrice", e.target.value)}
                placeholder={config ? String(config.fuel_baseline_price) : "3.69"}
                className="bg-zinc-950 border-zinc-700 h-9 mt-1"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Vehicle fuel efficiency</Label>
              <Select value={form.fuelProfile} onValueChange={(v) => setField("fuelProfile", v)}>
                <SelectTrigger data-testid="taxi-calc-fuel-profile" className="bg-zinc-950 border-zinc-700 h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                  {(config?.fuel_profiles || []).map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {Number(form.extraStops) > 0 && (
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Extra-stop locations</Label>
                <Input
                  data-testid="taxi-calc-stop-locations"
                  value={form.extraStopLocations}
                  onChange={(e) => setField("extraStopLocations", e.target.value)}
                  placeholder="Comma-separated stop addresses (informational)"
                  className="bg-zinc-950 border-zinc-700 h-9 mt-1"
                />
              </div>
            )}
            <div className="md:col-span-2 lg:col-span-3">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-400">Calculator notes (optional)</Label>
              <Textarea
                data-testid="taxi-calc-notes"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                rows={2}
                placeholder="Internal notes for this estimate — not attached to any booking"
                className="bg-zinc-950 border-zinc-700 mt-1"
              />
            </div>
          </div>

          {/* ---- Buttons ---- */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              type="button" data-testid="taxi-calc-calculate" onClick={calculate} disabled={loading}
              className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold uppercase tracking-wide"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />}
              Calculate Fare
            </Button>
            <Button
              type="button" data-testid="taxi-calc-copy-total" onClick={copyTotal} disabled={!result}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />Copy Total
            </Button>
            <Button
              type="button" data-testid="taxi-calc-copy-breakdown" onClick={copyBreakdown} disabled={!result}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />Copy Fare Breakdown
            </Button>
            <Button
              type="button" data-testid="taxi-calc-reset" onClick={resetCalculator}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset Calculator
            </Button>
            <Button
              type="button" data-testid="taxi-calc-print" onClick={printQuote} disabled={!result}
              variant="outline" className="bg-zinc-950 border-zinc-700 h-9 text-xs"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" />Print Quote
            </Button>
            <Button
              type="button" data-testid="taxi-calc-email" onClick={() => setEmailOpen((v) => !v)} disabled={!result}
              variant="outline" className="bg-zinc-950 border-sky-400/40 text-sky-200 hover:bg-sky-400/10 h-9 text-xs"
            >
              <Mail className="h-3.5 w-3.5 mr-1.5" />Email Quote
            </Button>
            {config?.can_edit_rates && (
              <Button
                type="button" data-testid="taxi-calc-edit-rates" onClick={() => setRateEditorOpen(true)}
                variant="outline" className="bg-zinc-950 border-amber-400/40 text-amber-200 hover:bg-amber-400/10 h-9 text-xs"
              >
                <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />Edit Rates
              </Button>
            )}

            {detached ? (
              <Button
                type="button" data-testid="taxi-calc-return" onClick={returnToConsole}
                className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold uppercase tracking-wide h-9 text-xs"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Return to Admin Console
              </Button>
            ) : (
              <Button
                type="button" data-testid="taxi-calc-detach" onClick={openDetached}
                title="Open the calculator in its own movable window"
                variant="outline" className="bg-zinc-950 border-indigo-400/40 text-indigo-200 hover:bg-indigo-400/10 h-9 text-xs"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                {detachedOpen ? "Focus Calculator Window" : "Detach Calculator"}
              </Button>
            )}
          </div>

          {/* Email-quote inline panel (never a modal; recompute + send happen server-side) */}
          {!detached && emailOpen && result && (
            <div data-testid="taxi-calc-email-panel" className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-sky-400/40 bg-sky-400/5 px-3 py-2">
              <Mail className="h-4 w-4 text-sky-300 flex-shrink-0" />
              <span className="text-[11px] uppercase tracking-wider text-sky-200">Email this quote to</span>
              <Input
                data-testid="taxi-calc-email-input"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendEmailQuote(); } }}
                placeholder="customer@email.com"
                className="bg-zinc-950 border-zinc-700 h-8 text-xs w-56"
              />
              <Button
                type="button" data-testid="taxi-calc-email-send" onClick={sendEmailQuote} disabled={emailSending}
                className="bg-sky-500 hover:bg-sky-400 text-white h-8 text-xs font-bold"
              >
                {emailSending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Send
              </Button>
              <Button
                type="button" data-testid="taxi-calc-email-cancel" onClick={() => { setEmailOpen(false); setEmailTo(""); }}
                variant="ghost" className="h-8 text-xs text-zinc-400 hover:text-zinc-200"
              >
                Cancel
              </Button>
              <span className="text-[10px] text-zinc-500 basis-full">A copy is BCC'd to dispatch@bullertaxi.com for records.</span>
            </div>
          )}

          {/* Popup-blocked inline notice (never a modal; never refreshes) */}
          {!detached && popupBlocked && (
            <div data-testid="taxi-calc-popup-blocked" className="mt-3 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              Your browser blocked the pop-out window. Allow pop-ups for this site, then click “Detach Calculator” again. The calculator here keeps working.
            </div>
          )}

          {/* ---- Error ---- */}
          {error && (
            <div data-testid="taxi-calc-error" className="mt-3 flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />{error}
            </div>
          )}

          {/* ---- Results ---- */}
          {result && (
            <div data-testid="taxi-calc-results" className="mt-4 rounded-lg border border-indigo-400/30 bg-zinc-950/60 p-4">
              {result.manual_quote_required ? (
                <div className="text-center py-4">
                  <div className="text-lg font-black text-amber-300 uppercase tracking-wide" data-testid="taxi-calc-total">
                    Manual Quote Required
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    Out-of-state destinations are not auto-priced. Provide a manual quote.
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2" data-testid="taxi-calc-version">
                    Pricing version {result.pricing_version} · {result.effective_date}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <span data-testid="taxi-calc-category" className="text-[11px] uppercase tracking-widest text-indigo-200 font-bold">
                      {CATEGORY_LABEL[result.category] || result.category}
                      {result.matched_destination ? ` · ${result.matched_destination}` : ""}
                    </span>
                    <span className="text-right">
                      <span className="block text-[10px] uppercase tracking-wider text-zinc-500">Final total</span>
                      <span data-testid="taxi-calc-total" className="block text-2xl font-black text-indigo-100">{usd(result.total)}</span>
                    </span>
                  </div>
                  <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                    <Row label="Destination rate" value={usd(li.base_fare)} />
                    <Row label="One-way distance" value={`${result.one_way_miles} mi`} />
                    <Row label="Total billable distance" value={`${result.total_trip_miles} mi`} />
                    <Row label="Estimated trip time" value={`${result.estimated_duration_minutes} min`} />
                    <Row label="Base fare" value={usd(li.base_fare)} />
                    <Row label="Additional-passenger" value={`${usd(li.additional_passenger_charge)} (${result.additional_passengers})`} />
                    <Row
                      label="Round-trip"
                      value={result.round_trip ? `×${li.round_trip_multiplier} → ${usd(li.base_and_passengers_billed)}` : "—"}
                    />
                    <Row label="Fuel surcharge" value={usd(li.fuel_surcharge?.amount)} />
                    <Row label="Waiting-time charge" value={usd(li.waiting_time?.amount)} />
                    <Row label="Extra-stop charge" value={usd(li.extra_stops?.amount)} />
                  </dl>
                  <div className="text-[10px] text-zinc-500 mt-3 pt-2 border-t border-zinc-800" data-testid="taxi-calc-version">
                    Pricing version {result.pricing_version} · effective {result.effective_date}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---- Quote history (session-scoped, newest first) ---- */}
          {history.length > 0 && (
            <div data-testid="taxi-calc-history" className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 font-bold">
                  <History className="h-3.5 w-3.5" /> Recent quotes
                </span>
                <Button type="button" data-testid="taxi-calc-history-clear" onClick={clearHistory} variant="ghost" className="h-6 px-2 text-[10px] text-zinc-500 hover:text-red-300">
                  <Trash2 className="h-3 w-3 mr-1" />Clear
                </Button>
              </div>
              <ul className="space-y-1.5">
                {history.map((e) => (
                  <li key={e.id} data-testid={`taxi-calc-history-item-${e.id}`} className="flex items-center gap-2 rounded border border-zinc-800 bg-zinc-950/60 px-2 py-1.5">
                    <span className="text-[10px] text-zinc-500 tabular-nums w-12 flex-shrink-0">
                      {new Date(e.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span className="text-[11px] text-zinc-200 truncate flex-1" title={e.label}>{e.label}</span>
                    <span className="text-xs font-bold text-indigo-200 tabular-nums flex-shrink-0">
                      {e.total === null ? "Manual" : usd(e.total)}
                    </span>
                    <button type="button" title="Copy total" data-testid={`taxi-calc-history-copy-total-${e.id}`} onClick={() => copyHistoryTotal(e)} className="text-zinc-500 hover:text-indigo-200 flex-shrink-0">
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" title="Copy full breakdown" data-testid={`taxi-calc-history-copy-details-${e.id}`} onClick={() => copyHistoryDetails(e)} className="text-zinc-500 hover:text-indigo-200 flex-shrink-0">
                      <ClipboardList className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* ---- Weatherford Special Rate Packages (isolated block) ---- */}
          {config && <SpecialRateCalculator config={config} />}
        </div>
      )}

      {/* Supervisor rate editor (mounted regardless of collapse state) */}
      {config?.can_edit_rates && (
        <RateEditorDialog open={rateEditorOpen} onOpenChange={setRateEditorOpen} config={config} onSaved={onRatesSaved} />
      )}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</dt>
      <dd className="font-semibold text-zinc-100">{value}</dd>
    </div>
  );
}
